import React from "react";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { LinkSimple, UploadSimple, Trash, Plus, ImageSquare, CaretUp, CaretDown } from "@phosphor-icons/react";
import { Input, Textarea, Select } from "../../components/ui";
import axiosInstance from "../../utils/axios/axiosConfig";

// Cached list of linkable destinations (pages, categories, brands, products, articles).
let _linksPromise = null;
const loadLinks = () => {
  if (!_linksPromise) {
    _linksPromise = axiosInstance.get("/sections/links").then((r) => r.data?.data || []).catch(() => []);
  }
  return _linksPromise;
};

function LinkField({ label, value, onChange }) {
  const [opts, setOpts] = React.useState([]);
  const listId = React.useMemo(() => "cms-links-" + Math.random().toString(36).slice(2, 9), []);
  React.useEffect(() => {
    let alive = true;
    loadLinks().then((o) => alive && setOpts(o));
    return () => { alive = false; };
  }, []);
  return (
    <>
      <Input
        label={label}
        icon={LinkSimple}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://… ou /chemin — ou choisir un lien existant"
        list={listId}
        autoComplete="off"
      />
      <datalist id={listId}>
        {opts.map((o, i) => (
          <option key={i} value={o.url}>{o.group ? `${o.group} · ${o.label}` : o.label}</option>
        ))}
      </datalist>
    </>
  );
}

const readFile = (file) =>
  new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });

function ImageField({ label, value, onChange }) {
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.warning("Image exceeds 15 MB.");
    onChange(await readFile(file));
    e.target.value = "";
  };
  return (
    <div>
      {label && <label className="mb-1.5 block text-sm font-medium text-ink/90">{label}</label>}
      <div className="flex items-center gap-3">
        <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl border border-line bg-surface-2">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <ImageSquare size={20} className="text-faint" />}
        </div>
        <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-sm font-medium text-ink transition hover:bg-surface-2">
          <UploadSimple size={15} /> Upload
          <input type="file" accept="image/*" className="hidden" onChange={onFile} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange(null)} className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-red-600 transition hover:bg-red-500/10">
            <Trash size={15} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

function ImageListField({ label, value = [], onChange }) {
  const list = Array.isArray(value) ? value : [];
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const added = [];
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) continue;
      added.push(await readFile(f));
    }
    onChange([...list, ...added]);
    e.target.value = "";
  };
  return (
    <div>
      {label && <label className="mb-1.5 block text-sm font-medium text-ink/90">{label}</label>}
      <div className="flex flex-wrap gap-2">
        {list.map((img, i) => (
          <div key={i} className="relative">
            <img src={img} alt="" className="h-20 w-20 rounded-lg border border-line object-cover" />
            <button type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))} className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-red-600 text-white shadow" aria-label="Remove">×</button>
          </div>
        ))}
        <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-lg border-2 border-dashed border-line bg-surface-2 text-faint transition hover:border-accent/50 hover:text-muted">
          <UploadSimple size={18} />
          <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
        </label>
      </div>
    </div>
  );
}

function RepeaterField({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const setItem = (i, name, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [name]: v } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      {field.label && <label className="mb-1.5 block text-sm font-medium text-ink/90">{field.label}</label>}
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="rounded-xl border border-line bg-surface-2 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-muted">{field.itemLabel || "Item"} {i + 1}</span>
              <div className="flex items-center gap-0.5">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="grid h-7 w-7 place-items-center rounded-lg text-muted transition hover:bg-surface enabled:hover:text-ink disabled:opacity-30"><CaretUp size={14} /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="grid h-7 w-7 place-items-center rounded-lg text-muted transition hover:bg-surface enabled:hover:text-ink disabled:opacity-30"><CaretDown size={14} /></button>
                <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="grid h-7 w-7 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600"><Trash size={14} /></button>
              </div>
            </div>
            <div className="space-y-3">
              {field.itemFields.map((f) => (
                <FieldRenderer key={f.name} field={f} value={item[f.name]} onChange={(v) => setItem(i, f.name, v)} />
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...items, {}])} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-sm font-medium text-muted transition hover:border-accent/50 hover:text-ink">
          <Plus size={15} /> Add {field.itemLabel || "item"}
        </button>
      </div>
    </div>
  );
}

export default function FieldRenderer({ field, value, onChange }) {
  switch (field.type) {
    case "text":
      return <Input label={field.label} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
    case "link":
      return <LinkField label={field.label} value={value} onChange={onChange} />;
    case "number":
      return <Input type="number" label={field.label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
    case "textarea":
      return <Textarea label={field.label} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "richtext":
      return (
        <div>
          {field.label && <label className="mb-1.5 block text-sm font-medium text-ink/90">{field.label}</label>}
          <div className="overflow-hidden rounded-xl border border-line">
            <ReactQuill theme="snow" value={value || ""} onChange={onChange} className="min-h-[160px]" />
          </div>
        </div>
      );
    case "select":
      return <Select label={field.label} options={field.options || []} value={value ?? null} onChange={onChange} />;
    case "image":
      return <ImageField label={field.label} value={value} onChange={onChange} />;
    case "image_list":
      return <ImageListField label={field.label} value={value} onChange={onChange} />;
    case "repeater":
      return <RepeaterField field={field} value={value} onChange={onChange} />;
    default:
      return null;
  }
}
